import { ReactNode } from 'react';

import styled from 'styled-components';

import { Image } from '@components/add-image/add-image';
import { CheckboxGroup } from '@components/checkbox-group';
import { EmbedVideo } from '@components/embed-video';
import { FieldMappingDisplay } from '@components/form-builder/fields/use-field-mapping';
import { PreviewFile, usePreviewFileModal } from '@components/modals/preview-file-modal';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { getManualDefaultValue } from '@components/text-editors/addons/field-mapping';
import { isMobileDevice } from '@utils/browser';
import { pxToRem } from '@utils/converters';
import { parseDate } from '@utils/date';
import { parseAcceptExtensions } from '@utils/file-types';
import { uuid } from '@utils/uuid';

import { ConditionalDisplay } from './conditions';
import { createFileTypeValidator } from './file-type-validation';
import { resolveFieldOptions } from './list-options';
import {
  FieldType,
  FormAnswerFieldType,
  FormAnswers,
  FormListsMap,
  FormV2,
  KENNITALA_LENGTH,
  SHORT_TEXT_MAX_LENGTH,
} from './types';

import { DefaultFile } from '../../file-field/types';
import { UploadedFile } from '../../file-field/uploaded-file';
import { createForm } from '../create-form';
import { FormValidatorType } from '../types';

const Form = createForm();

export type FieldWrapper = (field: FieldType, children: ReactNode) => ReactNode;

export const DynamicFields = ({
  fields,
  formAnswers,
  preview = false,
  fieldWrapper,
  lists = {},
}: {
  fields: FormV2['fields'];
  formAnswers?: FormAnswers;
  preview?: boolean;
  fieldWrapper?: FieldWrapper;
  lists?: FormListsMap;
}) => {
  return (
    <Stack space={4}>
      {fields.map((field) => (
        <RenderField
          key={field.id}
          field={field}
          preview={preview}
          formAnswers={formAnswers}
          fieldWrapper={fieldWrapper}
          lists={lists}
        />
      ))}
    </Stack>
  );
};

const getFormFieldForField = (
  field: FieldType,
  preview = false,
  formAnswers?: FormAnswers,
  fieldWrapper?: FieldWrapper,
  lists: FormListsMap = {}
) => {
  const fieldAnswer = formAnswers?.find((answer: FormAnswerFieldType) => answer.id === field.id);

  const validators: FormValidatorType[] =
    'isRequired' in field && field.isRequired ? ['required'] : [];

  switch (field.kind) {
    case 'submit': {
      return (
        <div>
          <Form.Actions>
            {field.saveLabel && !field.hideSaveButton && (
              <Form.Submit
                isDisabled={preview}
                text={field.saveLabel}
                variant="primary-blue"
                action="save"
                shouldDisableWhenNotDirty={true}
              />
            )}
            <Form.Submit isDisabled={preview} text={field.label} />
          </Form.Actions>
          <Form.Errors />
        </div>
      );
    }

    case 'input-date': {
      const restrictionType = field.dateRestrictionType ?? 'none';

      const minDate =
        restrictionType === 'range' && field.dateRangeStart
          ? parseDate(field.dateRangeStart)
          : undefined;
      const maxDate =
        restrictionType === 'range' && field.dateRangeEnd
          ? parseDate(field.dateRangeEnd)
          : undefined;

      const manualDefault = getManualDefaultValue(field.mappedDefaultValue);

      return (
        <Form.DatePicker
          label={field.label}
          description={field.description}
          id={field.id}
          validators={validators}
          placeholder={field.placeholder}
          defaultValue={field.value || manualDefault || field.defaultValue || undefined}
          disablePastDates={restrictionType === 'disablePast'}
          disableFutureDates={restrictionType === 'disableFuture'}
          minDate={minDate}
          maxDate={maxDate}
        />
      );
    }
    case 'input-file': {
      const fileFieldAnswer = fieldAnswer as Extract<FormAnswerFieldType, { kind: 'input-file' }>;
      const fileValues = Array.isArray(fileFieldAnswer?.value) ? fileFieldAnswer?.value : [];
      const originalAttachmentIds =
        typeof field.value === 'number' ? [field.value] : field.value || [];

      const files = fileValues.map((file, index) => {
        return {
          id: uuid(),
          name: file?.name ?? '',
          src: file?.src ?? '',
          attachmentId: originalAttachmentIds[index] as number,
        };
      });

      // The accept list is only advertised when it is also enforced, so the file
      // dialog's filter and the restriction can never disagree.
      const restrictedAccept =
        field.restrictTypes && parseAcceptExtensions(field.accept).length > 0
          ? field.accept
          : undefined;
      const fileValidators: FormValidatorType[] = restrictedAccept
        ? [...validators, createFileTypeValidator(restrictedAccept)]
        : validators;
      // Only a restricted field reports at pick time; elsewhere an empty or wrong
      // value is not obvious until submit.
      const validateOnChange = restrictedAccept ? true : undefined;

      if (field.allowMultiple || (field.maximumFileCount && field.maximumFileCount > 1)) {
        return (
          <Form.FileFieldMulti
            label={field.label}
            description={field.description}
            accept={restrictedAccept}
            id={field.id}
            validators={fileValidators}
            validateOnChange={validateOnChange}
            placeholder={field.placeholder}
            defaultValue={files.length > 0 ? files : undefined}
          />
        );
      }
      const defaultFile = files.length > 0 ? files[0] : undefined;
      const fileAnswer = fileValues[0];

      return (
        <SingleFileField
          field={field}
          validators={fileValidators}
          validateOnChange={validateOnChange}
          accept={restrictedAccept}
          defaultFile={defaultFile}
          fileAnswer={fileAnswer}
        />
      );
    }

    case 'input-phone': {
      const manualDefault = getManualDefaultValue(field.mappedDefaultValue);
      return (
        <Form.PhoneField
          id={field.id}
          label={field.label}
          description={field.description}
          validators={validators}
          isCountryPrefilled
          defaultValue={field.value || manualDefault || field.defaultValue || undefined}
        />
      );
    }

    case 'input-email': {
      const manualDefault = getManualDefaultValue(field.mappedDefaultValue);
      const resolvedDefault = field.value || manualDefault || field.defaultValue || undefined;
      return field.hasConfirmation && field.isRequired ? (
        <Stack space={4}>
          <Form.EmailTextField
            id={field.id}
            validators={validators}
            label={field.label}
            description={field.description}
            placeholder={field.placeholder}
            defaultValue={resolvedDefault}
            isDisabled={field.isReadOnly}
          />
          <Form.EmailTextField
            id={`${field.id}-confirmation`}
            validators={validators}
            label={field.confirmLabel}
            placeholder={field.placeholder}
            defaultValue={resolvedDefault}
            isDisabled={field.isReadOnly}
          />
        </Stack>
      ) : (
        <Form.EmailTextField
          id={field.id}
          validators={validators}
          label={field.label}
          description={field.description}
          placeholder={field.placeholder}
          defaultValue={resolvedDefault}
          isDisabled={field.isReadOnly}
        />
      );
    }

    case 'input-text-large': {
      return (
        <Form.Textarea
          id={field.id}
          label={field.label}
          description={field.description}
          validators={validators}
          placeholder={field.placeholder}
          defaultValue={field.value || field.defaultValue || ''}
          isDisabled={field.isReadOnly}
        />
      );
    }

    // Static default only: reading a mapped default here would surface an
    // unresolved `{{ … }}` as literal text when server-side resolution hasn't run.
    case 'input-text-small': {
      return (
        <Form.TextField
          validators={validators}
          id={field.id}
          label={field.label}
          description={field.description}
          placeholder={field.placeholder}
          maxLength={SHORT_TEXT_MAX_LENGTH}
          defaultValue={field.value || field.defaultValue || ''}
          isDisabled={field.isReadOnly}
        />
      );
    }
    case 'input-kennitala': {
      const kennitalaValidators: FormValidatorType[] = ['ssn', ...(validators ?? [])];
      return (
        <Form.TextField
          validators={kennitalaValidators}
          id={field.id}
          label={field.label}
          description={field.description}
          placeholder={field.placeholder}
          maxLength={KENNITALA_LENGTH}
          defaultValue={field.value || field.defaultValue || ''}
        />
      );
    }

    case 'input-number': {
      const manualDefaultRaw = getManualDefaultValue(field.mappedDefaultValue);
      const manualDefault =
        manualDefaultRaw != null && manualDefaultRaw !== '' ? Number(manualDefaultRaw) : null;
      const staticDefault =
        typeof field.defaultValue === 'number' && Number.isFinite(field.defaultValue)
          ? field.defaultValue
          : null;
      const resolvedDefault =
        field.value != null
          ? field.value
          : manualDefault != null && Number.isFinite(manualDefault)
          ? manualDefault
          : staticDefault;
      return (
        <Form.NumberField
          validators={validators}
          id={field.id}
          label={field.label}
          description={field.description}
          placeholder={field.placeholder ?? ''}
          min={field.restrictMin && field.min !== null ? field.min : undefined}
          max={field.restrictMax && field.max !== null ? field.max : undefined}
          disableNegative={!field.allowNegative}
          isDecimal={field.isDecimal}
          defaultValue={resolvedDefault}
        />
      );
    }

    case 'video': {
      if (!field.url) return null;
      return <EmbedVideo url={field.url} />;
    }

    case 'section': {
      if (field.fields.length === 0) {
        return null;
      }

      return (
        <SectionContainer data-columns={field.columns} data-kind="section">
          {field.fields.map((child) => (
            <RenderField
              key={child.id}
              field={child}
              preview={preview}
              formAnswers={formAnswers}
              fieldWrapper={fieldWrapper}
              lists={lists}
            />
          ))}
        </SectionContainer>
      );
    }

    case 'input-dropdown': {
      const options = resolveFieldOptions(field, lists);
      const rawDefault = field.value || field.defaultValue || undefined;
      return (
        <Form.Select
          id={field.id}
          label={field.label}
          description={field.description}
          options={options}
          defaultValue={resolveOptionValue(rawDefault, options)}
          validators={validators}
          placeholder={field.placeholder}
          isSearchable={!isMobileDevice}
        />
      );
    }

    case 'input-single-choice': {
      const options = resolveFieldOptions(field, lists);
      const rawDefault = field.value || field.defaultValue || undefined;
      return (
        <Form.RadioGroup
          id={field.id}
          label={field.label}
          description={field.description}
          options={options}
          defaultValue={resolveOptionValue(rawDefault, options)}
          validators={validators}
        />
      );
    }

    case 'input-multiple-choice': {
      const options = resolveFieldOptions(field, lists);
      const rawDefaults = field.value?.length ? field.value : field.defaultValue ?? undefined;
      const matchedDefaults = rawDefaults
        ?.map((value) => resolveOptionValue(value, options))
        .filter((value): value is string => value !== undefined);
      return (
        <Form.CheckboxGroup
          id={field.id}
          label={field.label}
          description={field.description}
          defaultValue={matchedDefaults}
          validators={validators}
        >
          {options.map((option) => {
            return (
              <CheckboxGroup.Item key={option.value} label={option.label} value={option.value} />
            );
          })}
        </Form.CheckboxGroup>
      );
    }

    case 'heading': {
      return <FieldMappingDisplay content={field.label} textProps={{ font: 'heading1' }} />;
    }

    case 'subheading': {
      return <FieldMappingDisplay content={field.label} textProps={{ font: 'heading2' }} />;
    }

    case 'separator': {
      return <Separator />;
    }

    case 'html': {
      return <FieldMappingDisplay content={field.content} useRichText />;
    }

    case 'image':
      return field.url ? <Image src={field.url} $width={field.imageWidth} /> : null;

    case 'content-file':
      return field.url ? (
        <ContentFileDisplay
          url={field.url}
          urlConverted={field.urlConverted}
          fileName={field.fileAlias || field.fileName}
          originalFileName={field.fileName}
          label={field.label}
        />
      ) : null;

    default:
      return null;
  }
};

const ContentFileDisplay = ({
  url,
  urlConverted,
  fileName,
  originalFileName,
  label,
}: {
  url: string;
  urlConverted?: string;
  fileName: string;
  originalFileName: string;
  label?: string;
}) => {
  const showPreview = usePreviewFileModal();

  return (
    <Stack space={1}>
      {label && <Text font="bold14" text={label} />}
      <UploadedFile
        name={fileName}
        path={url}
        onPreview={() =>
          showPreview({
            formFile: { name: fileName, src: url, srcConverted: urlConverted, originalFileName },
          })
        }
      />
    </Stack>
  );
};

const SingleFileField = ({
  field,
  validators,
  validateOnChange,
  accept,
  defaultFile,
  fileAnswer,
}: {
  field: Extract<FieldType, { kind: 'input-file' }>;
  validators: FormValidatorType[];
  validateOnChange?: boolean;
  accept?: string;
  defaultFile?: DefaultFile;
  fileAnswer?: Partial<PreviewFile>;
}) => {
  const showPreview = usePreviewFileModal();
  const hasMappedDefault = Boolean(field.mappedDefaultValue?.value);

  const handlePreview =
    hasMappedDefault && fileAnswer?.src
      ? () =>
          showPreview({
            formFile: {
              name: fileAnswer.name ?? defaultFile!.name,
              src: fileAnswer.src!,
              srcConverted: fileAnswer.srcConverted,
              originalFileName: fileAnswer.name ?? defaultFile!.name,
            },
          })
      : undefined;

  return (
    <Form.FileField
      label={field.label}
      description={field.description}
      accept={accept}
      id={field.id}
      placeholder={field.placeholder}
      validators={validators}
      validateOnChange={validateOnChange}
      defaultValue={defaultFile}
      allowReplace={hasMappedDefault}
      hideRemove={hasMappedDefault}
      onPreview={handlePreview}
      onClick={handlePreview}
    />
  );
};

const RenderField = ({
  field,
  preview,
  formAnswers,
  fieldWrapper,
  lists = {},
}: {
  field: FieldType;
  preview?: boolean;
  formAnswers?: FormAnswers;
  fieldWrapper?: FieldWrapper;
  lists?: FormListsMap;
}) => {
  const formField = getFormFieldForField(field, preview, formAnswers, fieldWrapper, lists);

  // Apply the wrapper before the empty check: a field can render nothing on its
  // own (e.g. a content-file mapped to a variable has no file in the builder)
  // yet still get content from the preview wrapper (its mapped-value card).
  const wrapped = fieldWrapper ? fieldWrapper(field, formField) : formField;

  if (!wrapped) return null;

  if (field.kind === 'submit') {
    return <>{wrapped}</>;
  }

  return <ConditionalDisplay field={field}>{wrapped}</ConditionalDisplay>;
};

/**
 * Translates a mapped default to its option value if needed. Returns
 * undefined when the value doesn't match any option (by value or label) so
 * the rendered control falls back to no selection — we never invent options
 * the field-builder didn't define.
 */
const resolveOptionValue = (
  raw: string | undefined,
  options: { value: string; label: string }[]
): string | undefined => {
  if (raw == null) return undefined;
  if (options.some((option) => option.value === raw)) return raw;
  const labelMatch = options.find((option) => option.label === raw);
  return labelMatch?.value;
};

const SectionContainer = styled.div({
  display: 'grid',
  gap: pxToRem(16),
  '[data-columns="2"] &': {
    gridColumn: '1 / span 2',
  },

  '[data-mobile="false"] &': {
    '&[data-columns="2"]': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
  },

  '[data-mobile="true"] &': {
    display: 'flex',
    flexDirection: 'column',
  },
});
